import { useMemo } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save,
  GitFork,
  Share2,
  Camera,
  History,
  Sparkles,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Grid,
  Box,
  Circle,
  Combine,
  Move,
  Layers,
  Minus,
  Maximize,
  Shrink,
  FlipHorizontal,
  Type,
  FileJson,
  Code,
  Braces,
  FunctionSquare,
  Library,
  Clapperboard,
  Lightbulb,
  Paintbrush,
  Settings,
  Grid3x3,
  Globe2,
  Bug,
  Ghost,
  Target,
  Ban,
  PackageX,
  Sun,
  PictureInPicture2,
  Shuffle,
  RefreshCcw,
  Upload,
  HardDriveDownload,
  Download,
  Compass,
  FilePlus,
  BookOpen,
  Undo,
  Redo,
  Rotate3D,
  Telescope,
  ScanLine,
  Triangle,
  Axis3d,
  Ruler,
  Play,
  Palette,
  LayoutGrid,
  Monitor,
  Tag,
  MousePointerClick,
  Orbit,
  Copy,
  FileCode,
  Zap as ZapIcon,
  AlignLeft,
  SlidersHorizontal,
  ToggleLeft,
  List,
  Grid2x2,
  Layers2,
  Hexagon,
  Radio,
  AlignJustify,
  Dot,
  FileText,
} from 'lucide-react';
import type { Command } from './types';
import type { CodeEditorRef } from '@/components/code/CodeEditor';
import type { ViewerState } from '@/components/viewer/toolbar/ViewerToolbar';
import { getShortcut } from './shortcuts';
import type { Parameter } from '@shared/types';
import {
  PRIMITIVES,
  TRANSFORMS,
  BOOLEAN_OPS,
  ADVANCED_OPS,
  EXTRUDE_OPS,
  CLONE_OPS,
  SNIPPETS,
  MODIFIER_OPS,
  RENDER_SETTINGS,
  LIGHTS,
  SCENE,
  CAMERAS,
  SCENE_GRIDS,
  LIBRARY_FEATURES,
  SCENE_CAMERA,
  SVG_OVERLAY,
  INTERACTION_FEATURES,
  MOTION_FEATURES,
  MESH_CLONE,
  MATERIAL_DEF,
  SHADER_MATERIAL,
  JS_SVG,
  PARAMETER_FEATURES,
} from '@/lib/scad-constants';
import { libraries } from '@/lib/libraries';
import type { ScadFeature } from '@/lib/scad-feature-types';
import { randomizeParameters } from '@/utils/randomizeUtils';
import { downloadOpenSCADFile } from '@/utils/downloadUtils';
import { downloadSTLFile } from '@/utils/downloadUtils';

export interface CommandRegistryOptions {
  // Editor
  editorRef: React.RefObject<CodeEditorRef | null>;
  // Actions / dialogs
  onSave?: () => void;
  onSaveAs?: () => void;
  onFork?: () => void;
  onShare?: () => void;
  onScreenshot?: () => void;
  onShowHistory?: () => void;
  onAiAssist?: () => void;
  onToggleVisibility?: () => void;
  isPublic?: boolean;
  isOwner?: boolean;
  hasModel?: boolean;
  // Viewer state
  viewerState?: ViewerState;
  onViewerStateChange?: (updater: (prev: ViewerState) => ViewerState) => void;
  ssaoEnabled?: boolean;
  onSSAOToggle?: (enabled: boolean) => void;
  bloomEnabled?: boolean;
  onBloomToggle?: (enabled: boolean) => void;
  softShadowsEnabled?: boolean;
  onSoftShadowsToggle?: (enabled: boolean) => void;
  onFitToBounds?: () => void;
  autoFit?: boolean;
  onAutoFitToggle?: (enabled: boolean) => void;
  // Parameters
  parameters?: Parameter[];
  onParametersChange?: (params: Parameter[]) => void;
  onCreateSnapshot?: () => void;
  onImportParameters?: () => void;
  // Blobs for download
  stlBlob?: Blob | null;
  scadCode?: string;
  // Files
  onUploadFile?: () => void;
  onCaptureMesh?: () => void;
  // Layout
  onLayoutChange?: (
    layout: 'code-preview' | 'all' | 'preview-parameters',
  ) => void;
  onProductMode?: () => void;
  onDevMode?: () => void;
  onRecompile?: () => void;
}

export function useCommandRegistry(opts: CommandRegistryOptions): Command[] {
  const navigate = useNavigate();

  return useMemo(() => {
    const {
      editorRef,
      onSave,
      onSaveAs,
      onFork,
      onShare,
      onScreenshot,
      onShowHistory,
      onAiAssist,
      onToggleVisibility,
      isPublic,
      isOwner,
      hasModel,
      viewerState,
      onViewerStateChange,
      ssaoEnabled,
      onSSAOToggle,
      bloomEnabled,
      onBloomToggle,
      softShadowsEnabled,
      onSoftShadowsToggle,
      onFitToBounds,
      autoFit,
      onAutoFitToggle,
      parameters = [],
      onParametersChange,
      onCreateSnapshot,
      onImportParameters,
      stlBlob,
      scadCode,
      onUploadFile,
      onCaptureMesh,
      onLayoutChange,
      onProductMode,
      onDevMode,
      onRecompile,
    } = opts;

    // Helper: insert code into editor
    const insert = (text: string) => editorRef.current?.insertText(text);
    const wrap = (before: string, after: string) =>
      editorRef.current?.wrapSelection(before, after);
    const ensureImport = (libName: string) => {
      const currentCode = editorRef.current?.getValue() || '';
      let importCode = '';
      if (libName === 'BOSL2') importCode = 'include <BOSL2/std.scad>\n';
      else if (libName === 'MCAD') importCode = 'include <MCAD/units.scad>\n';
      else if (libName === 'LEGO') importCode = 'use <LEGO/lego.scad>\n';
      else if (libName === 'BOSL')
        importCode = 'include <BOSL/constants.scad>\nuse <BOSL/shapes.scad>\n';
      else if (libName === 'gridfinity')
        importCode = 'include <gridfinity/gridfinity-rebuilt-bins.scad>\n';
      else importCode = `include <${libName}/std.scad>\n`;
      if (!currentCode.includes(`<${libName}/`)) {
        editorRef.current?.insertAtTop(importCode);
      }
    };

    // Execute a ScadFeature (simple insert or wrap)
    const execFeature = (feature: ScadFeature) => {
      if (feature.wrap) {
        wrap(feature.before || '', feature.after || '');
      } else if (feature.code) {
        insert(feature.code);
      }
    };

    // Viewer state helpers
    const setViewer = <K extends keyof ViewerState>(
      key: K,
      value: ViewerState[K],
    ) => onViewerStateChange?.((prev) => ({ ...prev, [key]: value }));

    const commands: Command[] = [];

    // ─────────────────────────────────────────
    // ACTIONS
    // ─────────────────────────────────────────

    if (onSave) {
      commands.push({
        id: 'action.save',
        label: 'Save',
        category: 'Actions',
        keywords: ['save', 'commit', 'update'],
        icon: Save,
        shortcut: getShortcut('action.save'),
        execute: onSave,
      });
    }

    if (onRecompile) {
      commands.push({
        id: 'action.recompile',
        label: 'Recompile',
        category: 'Actions',
        keywords: ['recompile', 'render', 'refresh', 'compile', 'f5'],
        icon: RefreshCcw,
        shortcut: getShortcut('action.recompile'),
        execute: onRecompile,
      });
    }

    if (onSaveAs) {
      commands.push({
        id: 'action.save-as',
        label: 'Save As New',
        category: 'Actions',
        keywords: ['save as', 'copy', 'new model'],
        icon: FilePlus,
        execute: onSaveAs,
      });
    }

    if (onFork) {
      commands.push({
        id: 'action.fork',
        label: 'Fork Model',
        category: 'Actions',
        keywords: ['fork', 'copy', 'duplicate', 'branch'],
        icon: GitFork,
        execute: onFork,
      });
    }

    if (onShare && hasModel) {
      commands.push({
        id: 'action.share',
        label: 'Share',
        category: 'Actions',
        keywords: ['share', 'link', 'url', 'embed'],
        icon: Share2,
        execute: onShare,
        available: () => !!hasModel,
      });
    }

    if (onScreenshot) {
      commands.push({
        id: 'action.screenshot',
        label: 'Screenshot',
        category: 'Actions',
        keywords: ['screenshot', 'capture', 'image', 'png', 'photo'],
        icon: Camera,
        shortcut: getShortcut('action.screenshot'),
        execute: onScreenshot,
      });
    }

    if (onShowHistory && hasModel) {
      commands.push({
        id: 'action.version-history',
        label: 'Version History',
        category: 'Actions',
        keywords: ['history', 'versions', 'restore', 'revert'],
        icon: History,
        execute: onShowHistory,
        available: () => !!hasModel,
      });
    }

    if (onAiAssist) {
      commands.push({
        id: 'action.ai-assistant',
        label: 'AI Assistant',
        category: 'Actions',
        keywords: ['ai', 'generate', 'gpt', 'llm', 'assistant', 'create'],
        icon: Sparkles,
        shortcut: getShortcut('action.ai-assistant'),
        execute: onAiAssist,
      });
    }

    if (onToggleVisibility && isOwner) {
      commands.push({
        id: 'action.toggle-visibility',
        label: isPublic ? 'Make Private' : 'Make Public',
        category: 'Actions',
        keywords: ['visibility', 'public', 'private', 'publish'],
        icon: isPublic ? Lock : Globe,
        getState: () => !!isPublic,
        execute: onToggleVisibility,
        available: () => !!isOwner,
      });
    }

    // Editor shortcuts
    commands.push({
      id: 'action.undo',
      label: 'Undo',
      category: 'Actions',
      keywords: ['undo', 'revert'],
      icon: Undo,
      execute: () => editorRef.current?.undo(),
    });

    commands.push({
      id: 'action.redo',
      label: 'Redo',
      category: 'Actions',
      keywords: ['redo', 'forward'],
      icon: Redo,
      execute: () => editorRef.current?.redo(),
    });

    // Layout
    if (onLayoutChange) {
      commands.push(
        {
          id: 'action.layout-code-preview',
          label: 'Layout: Code + Preview',
          category: 'Actions',
          keywords: ['layout', 'panel', 'view'],
          icon: LayoutGrid,
          shortcut: getShortcut('action.layout-code-preview'),
          execute: () => onLayoutChange('code-preview'),
        },
        {
          id: 'action.layout-all',
          label: 'Layout: All Panels',
          category: 'Actions',
          keywords: ['layout', 'panel', 'view', 'all'],
          icon: LayoutGrid,
          shortcut: getShortcut('action.layout-all'),
          execute: () => onLayoutChange('all'),
        },
        {
          id: 'action.layout-preview-params',
          label: 'Layout: Preview + Parameters',
          category: 'Actions',
          keywords: ['layout', 'panel', 'view', 'parameters'],
          icon: LayoutGrid,
          shortcut: getShortcut('action.layout-preview-params'),
          execute: () => onLayoutChange('preview-parameters'),
        },
      );
    }

    if (onProductMode) {
      commands.push({
        id: 'action.product-mode',
        label: 'Switch to Product Mode',
        category: 'Actions',
        keywords: ['product', 'mode', 'presentation', 'clean'],
        icon: Monitor,
        execute: onProductMode,
      });
    }

    if (onDevMode) {
      commands.push({
        id: 'action.dev-mode',
        label: 'Switch to Dev Mode',
        category: 'Actions',
        keywords: ['dev', 'developer', 'editor', 'code'],
        icon: Code,
        execute: onDevMode,
      });
    }

    // ─────────────────────────────────────────
    // INSERT CODE — 3D Primitives
    // ─────────────────────────────────────────

    commands.push(
      {
        id: 'insert.cube',
        label: 'Insert: Cube',
        category: 'Insert Code',
        keywords: ['cube', 'box', '3d', 'primitive', 'insert'],
        icon: Box,
        execute: () => execFeature(PRIMITIVES.cube as ScadFeature),
      },
      {
        id: 'insert.sphere',
        label: 'Insert: Sphere',
        category: 'Insert Code',
        keywords: ['sphere', 'ball', '3d', 'primitive', 'insert'],
        icon: Circle,
        execute: () => execFeature(PRIMITIVES.sphere as ScadFeature),
      },
      {
        id: 'insert.cylinder',
        label: 'Insert: Cylinder',
        category: 'Insert Code',
        keywords: ['cylinder', 'tube', '3d', 'primitive', 'insert'],
        icon: Layers,
        execute: () => execFeature(PRIMITIVES.cylinder as ScadFeature),
      },
      {
        id: 'insert.circle',
        label: 'Insert: Circle',
        category: 'Insert Code',
        keywords: ['circle', '2d', 'primitive', 'insert'],
        icon: Circle,
        execute: () => execFeature(PRIMITIVES.circle as ScadFeature),
      },
      {
        id: 'insert.square',
        label: 'Insert: Square',
        category: 'Insert Code',
        keywords: ['square', 'rectangle', '2d', 'primitive', 'insert'],
        icon: Box,
        execute: () => execFeature(PRIMITIVES.square as ScadFeature),
      },
      {
        id: 'insert.text',
        label: 'Insert: Text',
        category: 'Insert Code',
        keywords: ['text', 'string', '2d', 'primitive', 'insert'],
        icon: Type,
        execute: () => execFeature(PRIMITIVES.text as ScadFeature),
      },
    );

    // Boolean / advanced operations
    commands.push(
      {
        id: 'insert.union',
        label: 'Insert: Union',
        category: 'Insert Code',
        keywords: ['union', 'merge', 'boolean', 'insert'],
        icon: Combine,
        execute: () => execFeature(BOOLEAN_OPS.union as ScadFeature),
      },
      {
        id: 'insert.difference',
        label: 'Insert: Difference',
        category: 'Insert Code',
        keywords: ['difference', 'subtract', 'cut', 'boolean', 'insert'],
        icon: Minus,
        execute: () => execFeature(BOOLEAN_OPS.difference as ScadFeature),
      },
      {
        id: 'insert.intersection',
        label: 'Insert: Intersection',
        category: 'Insert Code',
        keywords: ['intersection', 'boolean', 'insert'],
        icon: Combine,
        execute: () => execFeature(BOOLEAN_OPS.intersection as ScadFeature),
      },
      {
        id: 'insert.hull',
        label: 'Insert: Hull',
        category: 'Insert Code',
        keywords: ['hull', 'convex', 'boolean', 'insert'],
        icon: Layers,
        execute: () => execFeature(ADVANCED_OPS.hull as ScadFeature),
      },
      {
        id: 'insert.minkowski',
        label: 'Insert: Minkowski',
        category: 'Insert Code',
        keywords: ['minkowski', 'boolean', 'insert'],
        icon: Box,
        execute: () => execFeature(ADVANCED_OPS.minkowski as ScadFeature),
      },
    );

    // Transforms
    commands.push(
      {
        id: 'insert.translate',
        label: 'Insert: Translate',
        category: 'Insert Code',
        keywords: ['translate', 'move', 'position', 'transform', 'insert'],
        icon: Move,
        execute: () => execFeature(TRANSFORMS.translate as ScadFeature),
      },
      {
        id: 'insert.rotate',
        label: 'Insert: Rotate',
        category: 'Insert Code',
        keywords: ['rotate', 'spin', 'angle', 'transform', 'insert'],
        icon: Rotate3D,
        execute: () => execFeature(TRANSFORMS.rotate as ScadFeature),
      },
      {
        id: 'insert.scale',
        label: 'Insert: Scale',
        category: 'Insert Code',
        keywords: ['scale', 'size', 'transform', 'insert'],
        icon: Maximize,
        execute: () => execFeature(TRANSFORMS.scale as ScadFeature),
      },
      {
        id: 'insert.resize',
        label: 'Insert: Resize',
        category: 'Insert Code',
        keywords: ['resize', 'size', 'transform', 'insert'],
        icon: Shrink,
        execute: () => execFeature(TRANSFORMS.resize as ScadFeature),
      },
      {
        id: 'insert.mirror',
        label: 'Insert: Mirror',
        category: 'Insert Code',
        keywords: ['mirror', 'flip', 'reflect', 'transform', 'insert'],
        icon: FlipHorizontal,
        execute: () => execFeature(TRANSFORMS.mirror as ScadFeature),
      },
      {
        id: 'insert.color',
        label: 'Insert: Color',
        category: 'Insert Code',
        keywords: ['color', 'colour', 'rgb', 'paint', 'insert'],
        icon: Palette,
        execute: () => execFeature(TRANSFORMS.color as ScadFeature),
      },
      {
        id: 'insert.material',
        label: 'Insert: Material',
        category: 'Insert Code',
        keywords: ['material', 'pbr', 'metalness', 'roughness', 'insert'],
        icon: Paintbrush,
        execute: () => execFeature(TRANSFORMS.material as ScadFeature),
      },
    );

    // Extrusions & clones
    commands.push(
      {
        id: 'insert.linear-extrude',
        label: 'Insert: Linear Extrude',
        category: 'Insert Code',
        keywords: ['linear', 'extrude', 'insert'],
        icon: Layers,
        execute: () => execFeature(EXTRUDE_OPS.linear_extrude as ScadFeature),
      },
      {
        id: 'insert.rotate-extrude',
        label: 'Insert: Rotate Extrude',
        category: 'Insert Code',
        keywords: ['rotate', 'extrude', 'lathe', 'insert'],
        icon: Rotate3D,
        execute: () => execFeature(EXTRUDE_OPS.rotate_extrude as ScadFeature),
      },
      {
        id: 'insert.clone-1d',
        label: 'Insert: 1D Clone (Line)',
        category: 'Insert Code',
        keywords: ['clone', 'array', 'repeat', '1d', 'line', 'insert'],
        icon: Layers,
        execute: () => execFeature(CLONE_OPS.clone_1d as ScadFeature),
      },
      {
        id: 'insert.clone-2d',
        label: 'Insert: 2D Clone (Grid)',
        category: 'Insert Code',
        keywords: ['clone', 'array', 'repeat', '2d', 'grid', 'insert'],
        icon: Grid3x3,
        execute: () => execFeature(CLONE_OPS.clone_2d as ScadFeature),
      },
      {
        id: 'insert.clone-3d',
        label: 'Insert: 3D Clone (Cube)',
        category: 'Insert Code',
        keywords: ['clone', 'array', 'repeat', '3d', 'cube', 'insert'],
        icon: Box,
        execute: () => execFeature(CLONE_OPS.clone_3d as ScadFeature),
      },
    );

    // Scene features
    commands.push(
      {
        id: 'insert.light-ambient',
        label: 'Insert: Ambient Light',
        category: 'Insert Code',
        keywords: ['light', 'ambient', 'scene', 'insert'],
        icon: Lightbulb,
        execute: () => execFeature(LIGHTS.ambient as ScadFeature),
      },
      {
        id: 'insert.light-directional',
        label: 'Insert: Directional Light',
        category: 'Insert Code',
        keywords: ['light', 'directional', 'scene', 'insert'],
        icon: Sun,
        execute: () => execFeature(LIGHTS.directional as ScadFeature),
      },
      {
        id: 'insert.light-point',
        label: 'Insert: Point Light',
        category: 'Insert Code',
        keywords: ['light', 'point', 'scene', 'insert'],
        icon: Lightbulb,
        execute: () => execFeature(LIGHTS.point as ScadFeature),
      },
      {
        id: 'insert.light-spot',
        label: 'Insert: Spot Light',
        category: 'Insert Code',
        keywords: ['light', 'spot', 'scene', 'insert'],
        icon: Lightbulb,
        execute: () => execFeature(LIGHTS.spot as ScadFeature),
      },
      {
        id: 'insert.scene-material',
        label: 'Insert: Scene Material',
        category: 'Insert Code',
        keywords: ['scene', 'material', 'insert'],
        icon: Paintbrush,
        execute: () => execFeature(SCENE.scene_material as ScadFeature),
      },
      {
        id: 'insert.scene-default-lights',
        label: 'Insert: Scene Default Lights',
        category: 'Insert Code',
        keywords: ['scene', 'light', 'default', 'insert'],
        icon: Lightbulb,
        execute: () => execFeature(SCENE.scene_default_lights as ScadFeature),
      },
      {
        id: 'insert.scene-shadows',
        label: 'Insert: Scene Shadows',
        category: 'Insert Code',
        keywords: ['scene', 'shadow', 'insert'],
        icon: Settings,
        execute: () => execFeature(SCENE.scene_shadows as ScadFeature),
      },
      {
        id: 'insert.scene-bloom',
        label: 'Insert: Scene Bloom',
        category: 'Insert Code',
        keywords: ['scene', 'bloom', 'glow', 'insert'],
        icon: Settings,
        execute: () => execFeature(SCENE.scene_bloom as ScadFeature),
      },
      {
        id: 'insert.scene-ssao',
        label: 'Insert: Scene SSAO',
        category: 'Insert Code',
        keywords: ['scene', 'ssao', 'ambient occlusion', 'insert'],
        icon: Settings,
        execute: () => execFeature(SCENE.scene_ssao as ScadFeature),
      },
      {
        id: 'insert.scene-background',
        label: 'Insert: Scene Background',
        category: 'Insert Code',
        keywords: ['scene', 'background', 'color', 'insert'],
        icon: Palette,
        execute: () => execFeature(SCENE.scene_background as ScadFeature),
      },
      {
        id: 'insert.plane-grid',
        label: 'Insert: Plane Grid',
        category: 'Insert Code',
        keywords: ['plane', 'grid', 'scene', 'insert'],
        icon: Grid3x3,
        execute: () => execFeature(SCENE_GRIDS.plane_grid as ScadFeature),
      },
      {
        id: 'insert.cube-wireframe',
        label: 'Insert: Cube Wireframe',
        category: 'Insert Code',
        keywords: ['cube', 'wireframe', 'scene', 'insert'],
        icon: Box,
        execute: () => execFeature(SCENE_GRIDS.cube_wireframe as ScadFeature),
      },
      {
        id: 'insert.sphere-wireframe',
        label: 'Insert: Sphere Wireframe',
        category: 'Insert Code',
        keywords: ['sphere', 'wireframe', 'scene', 'insert'],
        icon: Globe2,
        execute: () => execFeature(SCENE_GRIDS.sphere_wireframe as ScadFeature),
      },
      {
        id: 'insert.camera-pip',
        label: 'Insert: Camera PiP',
        category: 'Insert Code',
        keywords: ['camera', 'pip', 'picture in picture', 'insert'],
        icon: Clapperboard,
        execute: () => execFeature(CAMERAS.pip as ScadFeature),
      },
      {
        id: 'insert.camera-pip-ortho',
        label: 'Insert: Camera PiP Ortho',
        category: 'Insert Code',
        keywords: ['camera', 'pip', 'orthographic', 'insert'],
        icon: PictureInPicture2,
        execute: () => execFeature(CAMERAS.pip_ortho as ScadFeature),
      },
    );

    // New scene features: scene_camera, svg_overlay
    commands.push(
      {
        id: 'insert.scene-camera',
        label: 'Insert: Scene Camera',
        category: 'Insert Code',
        keywords: ['scene', 'camera', 'position', 'fov', 'insert'],
        icon: Camera,
        execute: () => execFeature(SCENE_CAMERA.scene_camera as ScadFeature),
      },
      {
        id: 'insert.svg-overlay',
        label: 'Insert: SVG Overlay',
        category: 'Insert Code',
        keywords: ['svg', 'overlay', 'logo', 'watermark', 'insert'],
        icon: FileCode,
        execute: () => execFeature(SVG_OVERLAY.svg_overlay as ScadFeature),
      },
    );

    // Interaction / motion features
    commands.push(
      {
        id: 'insert.annotation',
        label: 'Insert: Annotation',
        category: 'Insert Code',
        keywords: ['annotation', 'label', 'floating', 'text', 'insert'],
        icon: AlignLeft,
        execute: () =>
          execFeature(INTERACTION_FEATURES.annotation as ScadFeature),
      },
      {
        id: 'insert.interactive',
        label: 'Insert: Interactive (click to set)',
        category: 'Insert Code',
        keywords: ['interactive', 'click', 'clickable', 'parameter', 'insert'],
        icon: MousePointerClick,
        execute: () =>
          execFeature(INTERACTION_FEATURES.interactive as ScadFeature),
      },
      {
        id: 'insert.interactive-publish',
        label: 'Insert: Interactive Publish (pub/sub)',
        category: 'Insert Code',
        keywords: [
          'interactive',
          'publish',
          'pubsub',
          'click',
          'texture',
          'insert',
        ],
        icon: ZapIcon,
        execute: () =>
          execFeature(INTERACTION_FEATURES.interactive_publish as ScadFeature),
      },
      {
        id: 'insert.motion-translate',
        label: 'Insert: Motion – Translate (LFO)',
        category: 'Insert Code',
        keywords: ['motion', 'translate', 'animate', 'lfo', 'bounce', 'insert'],
        icon: Orbit,
        execute: () =>
          execFeature(MOTION_FEATURES.motion_translate as ScadFeature),
      },
      {
        id: 'insert.motion-orbit',
        label: 'Insert: Motion – Orbit',
        category: 'Insert Code',
        keywords: ['motion', 'orbit', 'revolve', 'animate', 'insert'],
        icon: Orbit,
        execute: () =>
          execFeature(MOTION_FEATURES.motion_orbit as ScadFeature),
      },
      {
        id: 'insert.motion-spin',
        label: 'Insert: Motion – Spin',
        category: 'Insert Code',
        keywords: ['motion', 'spin', 'rotate', 'continuous', 'animate', 'insert'],
        icon: Orbit,
        execute: () =>
          execFeature(MOTION_FEATURES.motion_spin as ScadFeature),
      },
    );

    // Mesh clone features
    commands.push(
      {
        id: 'insert.clone-ref',
        label: 'Insert: Clone Source (clone_ref)',
        category: 'Insert Code',
        keywords: ['clone', 'clone_ref', 'mesh', 'instancing', 'source', 'insert'],
        icon: Copy,
        execute: () => execFeature(MESH_CLONE.clone_ref as ScadFeature),
      },
      {
        id: 'insert.clone-instance',
        label: 'Insert: Clone Instance (clone)',
        category: 'Insert Code',
        keywords: ['clone', 'instance', 'mesh', 'copy', 'insert'],
        icon: Copy,
        execute: () => execFeature(MESH_CLONE.clone as ScadFeature),
      },
    );

    // Material def and shader material
    commands.push(
      {
        id: 'insert.material-def',
        label: 'Insert: Material Def (PBR)',
        category: 'Insert Code',
        keywords: ['material', 'material_def', 'pbr', 'metalness', 'roughness', 'insert'],
        icon: Sparkles,
        execute: () => execFeature(MATERIAL_DEF.material_def as ScadFeature),
      },
      {
        id: 'insert.shader-material',
        label: 'Insert: Shader Material (GLSL)',
        category: 'Insert Code',
        keywords: ['shader', 'shader_material', 'glsl', 'custom', 'insert'],
        icon: FileCode,
        execute: () =>
          execFeature(SHADER_MATERIAL.shader_material as ScadFeature),
      },
      {
        id: 'insert.js-svg',
        label: 'Insert: JS SVG Geometry Script',
        category: 'Insert Code',
        keywords: ['js', 'svg', 'js_svg', 'script', 'bezier', 'geometry', 'insert'],
        icon: FileCode,
        execute: () => execFeature(JS_SVG.js_svg as ScadFeature),
      },
    );

    // Snippets
    commands.push(
      {
        id: 'insert.module',
        label: 'Insert: Module',
        category: 'Insert Code',
        keywords: ['module', 'snippet', 'function', 'insert'],
        icon: Braces,
        execute: () => insert(SNIPPETS.module.code),
      },
      {
        id: 'insert.function',
        label: 'Insert: Function',
        category: 'Insert Code',
        keywords: ['function', 'snippet', 'insert'],
        icon: FunctionSquare,
        execute: () => insert(SNIPPETS.function.code),
      },
      {
        id: 'insert.for-loop',
        label: 'Insert: For Loop',
        category: 'Insert Code',
        keywords: ['for', 'loop', 'iterate', 'snippet', 'insert'],
        icon: Code,
        execute: () => insert(SNIPPETS.for_loop.code),
      },
      {
        id: 'insert.if-else',
        label: 'Insert: If/Else',
        category: 'Insert Code',
        keywords: ['if', 'else', 'conditional', 'snippet', 'insert'],
        icon: Code,
        execute: () => insert(SNIPPETS.if_else.code),
      },
      {
        id: 'insert.polygon',
        label: 'Insert: Polygon',
        category: 'Insert Code',
        keywords: ['polygon', '2d', 'shape', 'insert'],
        icon: Box,
        execute: () => insert(SNIPPETS.polygon.code),
      },
      {
        id: 'insert.polyhedron',
        label: 'Insert: Polyhedron',
        category: 'Insert Code',
        keywords: ['polyhedron', '3d', 'mesh', 'insert'],
        icon: Box,
        execute: () => insert(SNIPPETS.polyhedron.code),
      },
      {
        id: 'insert.parameter-block',
        label: 'Insert: Parameter Block',
        category: 'Insert Code',
        keywords: ['parameter', 'block', 'snippet', 'insert'],
        icon: FileJson,
        execute: () => insert(SNIPPETS.parameters.code),
      },
    );

    // Modifiers
    commands.push(
      {
        id: 'insert.modifier-debug',
        label: 'Insert: Debug Modifier (#)',
        category: 'Insert Code',
        keywords: ['debug', 'modifier', '#', 'insert'],
        icon: Bug,
        execute: () => execFeature(MODIFIER_OPS.debug as ScadFeature),
      },
      {
        id: 'insert.modifier-background',
        label: 'Insert: Background Modifier (%)',
        category: 'Insert Code',
        keywords: ['background', 'ghost', 'modifier', '%', 'insert'],
        icon: Ghost,
        execute: () => execFeature(MODIFIER_OPS.background as ScadFeature),
      },
      {
        id: 'insert.modifier-root',
        label: 'Insert: Root Modifier (!)',
        category: 'Insert Code',
        keywords: ['root', 'modifier', '!', 'insert'],
        icon: Target,
        execute: () => execFeature(MODIFIER_OPS.root as ScadFeature),
      },
      {
        id: 'insert.modifier-disable',
        label: 'Insert: Disable Modifier (*)',
        category: 'Insert Code',
        keywords: ['disable', 'modifier', '*', 'insert'],
        icon: Ban,
        execute: () => execFeature(MODIFIER_OPS.disable as ScadFeature),
      },
      {
        id: 'insert.modifier-exclude',
        label: 'Insert: Exclude From Export',
        category: 'Insert Code',
        keywords: ['exclude', 'export', 'modifier', 'insert'],
        icon: PackageX,
        execute: () =>
          execFeature(MODIFIER_OPS.exclude_from_export as ScadFeature),
      },
    );

    // Render settings
    commands.push(
      {
        id: 'insert.render-fn',
        label: 'Insert: Render Setting $fn',
        category: 'Insert Code',
        keywords: ['render', '$fn', 'resolution', 'quality', 'insert'],
        icon: Settings,
        execute: () => execFeature(RENDER_SETTINGS.render_fn as ScadFeature),
      },
      {
        id: 'insert.render-fa',
        label: 'Insert: Render Setting $fa',
        category: 'Insert Code',
        keywords: ['render', '$fa', 'angle', 'quality', 'insert'],
        icon: Settings,
        execute: () => execFeature(RENDER_SETTINGS.render_fa as ScadFeature),
      },
      {
        id: 'insert.render-fs',
        label: 'Insert: Render Setting $fs',
        category: 'Insert Code',
        keywords: ['render', '$fs', 'segment', 'quality', 'insert'],
        icon: Settings,
        execute: () => execFeature(RENDER_SETTINGS.render_fs as ScadFeature),
      },
    );

    // ─────────────────────────────────────────
    // INSERT CODE — Parameter widgets
    // ─────────────────────────────────────────

    // Numbers
    commands.push(
      {
        id: 'insert.param.number-slider',
        label: 'Parameter: Number Slider',
        category: 'Parameters',
        keywords: ['parameter', 'number', 'slider', 'insert'],
        icon: SlidersHorizontal,
        execute: () =>
          execFeature(PARAMETER_FEATURES.number_slider as ScadFeature),
      },
      {
        id: 'insert.param.number-slider-range',
        label: 'Parameter: Slider with Range',
        category: 'Parameters',
        keywords: ['parameter', 'number', 'slider', 'range', 'insert'],
        icon: SlidersHorizontal,
        execute: () =>
          execFeature(PARAMETER_FEATURES.number_slider_range as ScadFeature),
      },
      {
        id: 'insert.param.number-slider-step',
        label: 'Parameter: Slider with Step',
        category: 'Parameters',
        keywords: ['parameter', 'number', 'slider', 'step', 'insert'],
        icon: SlidersHorizontal,
        execute: () =>
          execFeature(PARAMETER_FEATURES.number_slider_step as ScadFeature),
      },
      {
        id: 'insert.param.number-slider-extended',
        label: 'Parameter: Slider with Extended Range',
        category: 'Parameters',
        keywords: ['parameter', 'number', 'slider', 'extended', 'range', 'insert'],
        icon: SlidersHorizontal,
        execute: () =>
          execFeature(
            PARAMETER_FEATURES.number_slider_extended_range as ScadFeature,
          ),
      },
      {
        id: 'insert.param.angle-dial',
        label: 'Parameter: Angle Dial',
        category: 'Parameters',
        keywords: ['parameter', 'angle', 'dial', 'rotation', 'insert'],
        icon: Rotate3D,
        execute: () =>
          execFeature(PARAMETER_FEATURES.angle_dial as ScadFeature),
      },
      {
        id: 'insert.param.stepper',
        label: 'Parameter: Stepper',
        category: 'Parameters',
        keywords: ['parameter', 'stepper', 'step', 'discrete', 'insert'],
        icon: List,
        execute: () =>
          execFeature(PARAMETER_FEATURES.stepper as ScadFeature),
      },
      {
        id: 'insert.param.range-input',
        label: 'Parameter: Range Input',
        category: 'Parameters',
        keywords: ['parameter', 'range', 'min', 'max', 'insert'],
        icon: Dot,
        execute: () =>
          execFeature(PARAMETER_FEATURES.range_input as ScadFeature),
      },
    );

    // Text & Toggles
    commands.push(
      {
        id: 'insert.param.string-input',
        label: 'Parameter: String Input',
        category: 'Parameters',
        keywords: ['parameter', 'string', 'text', 'input', 'insert'],
        icon: AlignLeft,
        execute: () =>
          execFeature(PARAMETER_FEATURES.string_input as ScadFeature),
      },
      {
        id: 'insert.param.boolean-toggle',
        label: 'Parameter: Boolean Toggle',
        category: 'Parameters',
        keywords: ['parameter', 'boolean', 'toggle', 'checkbox', 'insert'],
        icon: ToggleLeft,
        execute: () =>
          execFeature(PARAMETER_FEATURES.boolean_toggle as ScadFeature),
      },
      {
        id: 'insert.param.font-picker',
        label: 'Parameter: Font Picker',
        category: 'Parameters',
        keywords: ['parameter', 'font', 'typeface', 'text', 'insert'],
        icon: Type,
        execute: () =>
          execFeature(PARAMETER_FEATURES.font_picker as ScadFeature),
      },
      {
        id: 'insert.param.file-upload',
        label: 'Parameter: File Upload',
        category: 'Parameters',
        keywords: ['parameter', 'file', 'upload', 'svg', 'dxf', 'insert'],
        icon: FileText,
        execute: () =>
          execFeature(PARAMETER_FEATURES.file_upload as ScadFeature),
      },
    );

    // Dropdowns
    commands.push(
      {
        id: 'insert.param.number-dropdown',
        label: 'Parameter: Number Dropdown',
        category: 'Parameters',
        keywords: ['parameter', 'number', 'dropdown', 'select', 'insert'],
        icon: List,
        execute: () =>
          execFeature(PARAMETER_FEATURES.number_dropdown as ScadFeature),
      },
      {
        id: 'insert.param.labelled-dropdown',
        label: 'Parameter: Labelled Dropdown',
        category: 'Parameters',
        keywords: ['parameter', 'labelled', 'dropdown', 'select', 'insert'],
        icon: List,
        execute: () =>
          execFeature(PARAMETER_FEATURES.labelled_dropdown as ScadFeature),
      },
      {
        id: 'insert.param.string-dropdown',
        label: 'Parameter: String Dropdown',
        category: 'Parameters',
        keywords: ['parameter', 'string', 'dropdown', 'select', 'insert'],
        icon: List,
        execute: () =>
          execFeature(PARAMETER_FEATURES.string_dropdown as ScadFeature),
      },
    );

    // Colors
    commands.push(
      {
        id: 'insert.param.color-picker',
        label: 'Parameter: Color Picker',
        category: 'Parameters',
        keywords: ['parameter', 'color', 'colour', 'picker', 'insert'],
        icon: Palette,
        execute: () =>
          execFeature(PARAMETER_FEATURES.color_picker as ScadFeature),
      },
      {
        id: 'insert.param.color-picker-alpha',
        label: 'Parameter: Color Picker with Alpha',
        category: 'Parameters',
        keywords: ['parameter', 'color', 'colour', 'alpha', 'opacity', 'insert'],
        icon: Palette,
        execute: () =>
          execFeature(PARAMETER_FEATURES.color_picker_alpha as ScadFeature),
      },
      {
        id: 'insert.param.color-swatches',
        label: 'Parameter: Color Swatches',
        category: 'Parameters',
        keywords: ['parameter', 'color', 'swatches', 'palette', 'insert'],
        icon: Palette,
        execute: () =>
          execFeature(PARAMETER_FEATURES.color_swatches as ScadFeature),
      },
    );

    // Vectors & Pads
    commands.push(
      {
        id: 'insert.param.vector-sliders',
        label: 'Parameter: Vector Sliders',
        category: 'Parameters',
        keywords: ['parameter', 'vector', 'sliders', 'xyz', 'insert'],
        icon: SlidersHorizontal,
        execute: () =>
          execFeature(PARAMETER_FEATURES.vector_sliders as ScadFeature),
      },
      {
        id: 'insert.param.xy-pad',
        label: 'Parameter: XY Pad',
        category: 'Parameters',
        keywords: ['parameter', 'xy', 'pad', '2d', 'position', 'insert'],
        icon: Grid2x2,
        execute: () =>
          execFeature(PARAMETER_FEATURES.xy_pad as ScadFeature),
      },
      {
        id: 'insert.param.xyz-pad',
        label: 'Parameter: XYZ Pad',
        category: 'Parameters',
        keywords: ['parameter', 'xyz', 'pad', '3d', 'position', 'insert'],
        icon: Grid2x2,
        execute: () =>
          execFeature(PARAMETER_FEATURES.xyz_pad as ScadFeature),
      },
      {
        id: 'insert.param.polygon-pad',
        label: 'Parameter: Polygon Pad',
        category: 'Parameters',
        keywords: ['parameter', 'polygon', 'pad', 'vertices', 'shape', 'insert'],
        icon: Hexagon,
        execute: () =>
          execFeature(PARAMETER_FEATURES.polygon_pad as ScadFeature),
      },
    );

    // Special Inputs
    commands.push(
      {
        id: 'insert.param.grid-input',
        label: 'Parameter: Grid Input',
        category: 'Parameters',
        keywords: ['parameter', 'grid', 'matrix', 'cells', 'insert'],
        icon: Grid3x3,
        execute: () =>
          execFeature(PARAMETER_FEATURES.grid_input as ScadFeature),
      },
      {
        id: 'insert.param.stack-input',
        label: 'Parameter: Stack Input',
        category: 'Parameters',
        keywords: ['parameter', 'stack', 'layers', 'list', 'insert'],
        icon: Layers2,
        execute: () =>
          execFeature(PARAMETER_FEATURES.stack_input as ScadFeature),
      },
      {
        id: 'insert.param.svg-checkbox',
        label: 'Parameter: SVG Checkbox',
        category: 'Parameters',
        keywords: ['parameter', 'svg', 'checkbox', 'group', 'picker', 'insert'],
        icon: Radio,
        execute: () =>
          execFeature(PARAMETER_FEATURES.svg_checkbox as ScadFeature),
      },
      {
        id: 'insert.param.svg-radio',
        label: 'Parameter: SVG Radio',
        category: 'Parameters',
        keywords: ['parameter', 'svg', 'radio', 'group', 'picker', 'insert'],
        icon: Radio,
        execute: () =>
          execFeature(PARAMETER_FEATURES.svg_radio as ScadFeature),
      },
    );

    // Organization
    commands.push(
      {
        id: 'insert.param.parameter-groups',
        label: 'Parameter: Groups',
        category: 'Parameters',
        keywords: ['parameter', 'group', 'organize', 'section', 'insert'],
        icon: AlignJustify,
        execute: () =>
          execFeature(PARAMETER_FEATURES.parameter_groups as ScadFeature),
      },
      {
        id: 'insert.param.tab-groups',
        label: 'Parameter: Tab Groups',
        category: 'Parameters',
        keywords: ['parameter', 'tab', 'group', 'tabs', 'organize', 'insert'],
        icon: AlignJustify,
        execute: () =>
          execFeature(PARAMETER_FEATURES.tab_groups as ScadFeature),
      },
      {
        id: 'insert.param.stop-parsing',
        label: 'Parameter: Stop Parsing',
        category: 'Parameters',
        keywords: ['parameter', 'stop', 'parsing', 'separator', 'insert'],
        icon: Minus,
        execute: () =>
          execFeature(PARAMETER_FEATURES.stop_parsing as ScadFeature),
      },
      {
        id: 'insert.param.markdown-block',
        label: 'Parameter: Markdown Block',
        category: 'Parameters',
        keywords: ['parameter', 'markdown', 'text', 'description', 'insert'],
        icon: AlignLeft,
        execute: () =>
          execFeature(PARAMETER_FEATURES.markdown_block as ScadFeature),
      },
      {
        id: 'insert.param.snapshots',
        label: 'Parameter: Snapshots',
        category: 'Parameters',
        keywords: ['parameter', 'snapshot', 'preset', 'save', 'insert'],
        icon: Camera,
        execute: () =>
          execFeature(PARAMETER_FEATURES.snapshots as ScadFeature),
      },
    );

    // Annotations
    commands.push(
      {
        id: 'insert.param.visibility-expr',
        label: 'Parameter: Visibility Expression',
        category: 'Parameters',
        keywords: ['parameter', 'visibility', 'show', 'hide', 'conditional', 'insert'],
        icon: Eye,
        execute: () =>
          execFeature(PARAMETER_FEATURES.visibility_expr as ScadFeature),
      },
      {
        id: 'insert.param.lock-annotation',
        label: 'Parameter: Lock Annotation',
        category: 'Parameters',
        keywords: ['parameter', 'lock', 'readonly', 'annotation', 'insert'],
        icon: Lock,
        execute: () =>
          execFeature(PARAMETER_FEATURES.lock_annotation as ScadFeature),
      },
      {
        id: 'insert.param.pubsub-annotation',
        label: 'Parameter: Pub/Sub Annotation',
        category: 'Parameters',
        keywords: ['parameter', 'pubsub', 'publish', 'subscribe', 'texture', 'insert'],
        icon: ZapIcon,
        execute: () =>
          execFeature(PARAMETER_FEATURES.pubsub_annotation as ScadFeature),
      },
    );

    // Library includes
    libraries.forEach((lib) => {
      const displayName = (
        'displayName' in lib ? lib.displayName : lib.name
      ) as string;
      commands.push({
        id: `insert.lib-include-${lib.name}`,
        label: `Include Library: ${displayName}`,
        category: 'Insert Code',
        keywords: ['library', 'include', lib.name.toLowerCase(), 'import'],
        icon: Library,
        execute: () => ensureImport(lib.name),
      });

      // Library feature snippets
      const features = LIBRARY_FEATURES[lib.name] || [];
      features.forEach((feat) => {
        commands.push({
          id: `insert.lib-${lib.name}-${feat.id}`,
          label: `${displayName}: ${feat.label}`,
          category: 'Insert Code',
          keywords: [
            lib.name.toLowerCase(),
            feat.label.toLowerCase(),
            'library',
            'insert',
          ],
          icon: Box,
          execute: () => {
            ensureImport(lib.name);
            execFeature(feat as ScadFeature);
          },
        });
      });
    });

    // ─────────────────────────────────────────
    // VIEWER TOGGLES
    // ─────────────────────────────────────────

    if (viewerState && onViewerStateChange) {
      commands.push(
        {
          id: 'viewer.grid',
          label: 'Viewer: Grid',
          category: 'Viewer',
          keywords: ['grid', 'ground', 'viewer', 'toggle'],
          icon: Grid,
          shortcut: getShortcut('viewer.grid'),
          getState: () => viewerState.showGrid,
          execute: () => setViewer('showGrid', !viewerState.showGrid),
        },
        {
          id: 'viewer.axis',
          label: 'Viewer: Axis Helper',
          category: 'Viewer',
          keywords: ['axis', 'xyz', 'helper', 'viewer', 'toggle'],
          icon: Axis3d,
          shortcut: getShortcut('viewer.axis'),
          getState: () => viewerState.showAxis,
          execute: () => setViewer('showAxis', !viewerState.showAxis),
        },
        {
          id: 'viewer.orthographic',
          label: 'Viewer: Orthographic / Perspective',
          category: 'Viewer',
          keywords: [
            'orthographic',
            'perspective',
            'camera',
            'projection',
            'viewer',
            'toggle',
          ],
          icon: Telescope,
          shortcut: getShortcut('viewer.orthographic'),
          getState: () => viewerState.isOrthographic,
          execute: () =>
            setViewer('isOrthographic', !viewerState.isOrthographic),
        },
        {
          id: 'viewer.wireframe',
          label: 'Viewer: Wireframe',
          category: 'Viewer',
          keywords: ['wireframe', 'mesh', 'viewer', 'toggle'],
          icon: ScanLine,
          shortcut: getShortcut('viewer.wireframe'),
          getState: () => viewerState.wireframeOpacity > 0,
          execute: () =>
            setViewer(
              'wireframeOpacity',
              viewerState.wireframeOpacity > 0 ? 0 : 0.3,
            ),
        },
        {
          id: 'viewer.polygonMode',
          label: 'Viewer: Polygon Mode',
          category: 'Viewer',
          keywords: [
            'polygon',
            'mesh',
            'triangle',
            'faces',
            'viewer',
            'toggle',
          ],
          icon: Triangle,
          shortcut: getShortcut('viewer.polygonMode'),
          getState: () => viewerState.polygonMode,
          execute: () => setViewer('polygonMode', !viewerState.polygonMode),
        },
        {
          id: 'viewer.grayscale',
          label: 'Viewer: Grayscale Mode',
          category: 'Viewer',
          keywords: [
            'grayscale',
            'grey',
            'gray',
            'flat',
            'debug',
            'geometry',
            'viewer',
            'toggle',
          ],
          icon: Palette,
          shortcut: getShortcut('viewer.grayscale'),
          getState: () => viewerState.grayscaleMode,
          execute: () => setViewer('grayscaleMode', !viewerState.grayscaleMode),
        },
        {
          id: 'viewer.multiview',
          label: 'Viewer: Multi-View',
          category: 'Viewer',
          keywords: ['multi', 'view', 'split', 'quadrant', 'viewer', 'toggle'],
          icon: LayoutGrid,
          getState: () => viewerState.multiView,
          execute: () => setViewer('multiView', !viewerState.multiView),
        },
        {
          id: 'viewer.moving-light',
          label: 'Viewer: Moving Light',
          category: 'Viewer',
          keywords: [
            'moving',
            'light',
            'orbit',
            'animated',
            'viewer',
            'toggle',
          ],
          icon: Sun,
          getState: () => viewerState.showMovingLight,
          execute: () =>
            setViewer('showMovingLight', !viewerState.showMovingLight),
        },
        {
          id: 'viewer.presentation',
          label: 'Viewer: Presentation Mode',
          category: 'Viewer',
          keywords: [
            'presentation',
            'rotate',
            'auto',
            'spin',
            'viewer',
            'toggle',
          ],
          icon: Play,
          shortcut: getShortcut('viewer.presentation'),
          getState: () => viewerState.presentationMode,
          execute: () =>
            setViewer('presentationMode', !viewerState.presentationMode),
        },
        {
          id: 'viewer.measurement',
          label: 'Viewer: Measurement',
          category: 'Viewer',
          keywords: ['measure', 'ruler', 'distance', 'viewer', 'toggle'],
          icon: Ruler,
          shortcut: getShortcut('viewer.measurement'),
          getState: () => viewerState.measurementEnabled,
          execute: () =>
            setViewer('measurementEnabled', !viewerState.measurementEnabled),
        },
        {
          id: 'viewer.inspector',
          label: 'Viewer: Object Inspector',
          category: 'Viewer',
          keywords: [
            'inspector',
            'inspect',
            'object',
            'info',
            'viewer',
            'toggle',
          ],
          icon: Target,
          shortcut: getShortcut('viewer.inspector'),
          getState: () => viewerState.inspectorEnabled,
          execute: () =>
            setViewer('inspectorEnabled', !viewerState.inspectorEnabled),
        },
        {
          id: 'viewer.annotations',
          label: 'Viewer: Annotations',
          category: 'Viewer',
          keywords: ['annotations', 'labels', 'overlay', 'viewer', 'toggle'],
          icon: Tag,
          shortcut: getShortcut('viewer.annotations'),
          getState: () => viewerState.annotationsEnabled,
          execute: () =>
            setViewer('annotationsEnabled', !viewerState.annotationsEnabled),
        },
        {
          id: 'viewer.excluded',
          label: 'Viewer: Show Excluded Objects',
          category: 'Viewer',
          keywords: ['excluded', 'hidden', 'show', 'viewer', 'toggle'],
          icon: viewerState.showExcluded ? Eye : EyeOff,
          shortcut: getShortcut('viewer.excluded'),
          getState: () => viewerState.showExcluded,
          execute: () => setViewer('showExcluded', !viewerState.showExcluded),
        },
        {
          id: 'viewer.pip',
          label: 'Viewer: Picture-in-Picture',
          category: 'Viewer',
          keywords: ['pip', 'picture in picture', 'camera', 'viewer', 'toggle'],
          icon: PictureInPicture2,
          getState: () => viewerState.showPiP,
          execute: () => setViewer('showPiP', !viewerState.showPiP),
        },
      );
    }

    if (ssaoEnabled !== undefined && onSSAOToggle) {
      commands.push({
        id: 'viewer.ssao',
        label: 'Viewer: SSAO (Ambient Occlusion)',
        category: 'Viewer',
        keywords: ['ssao', 'ambient occlusion', 'shadow', 'viewer', 'toggle'],
        icon: Settings,
        getState: () => !!ssaoEnabled,
        execute: () => onSSAOToggle(!ssaoEnabled),
      });
    }

    if (bloomEnabled !== undefined && onBloomToggle) {
      commands.push({
        id: 'viewer.bloom',
        label: 'Viewer: Bloom',
        category: 'Viewer',
        keywords: ['bloom', 'glow', 'post processing', 'viewer', 'toggle'],
        icon: Sparkles,
        getState: () => !!bloomEnabled,
        execute: () => onBloomToggle(!bloomEnabled),
      });
    }

    if (softShadowsEnabled !== undefined && onSoftShadowsToggle) {
      commands.push({
        id: 'viewer.soft-shadows',
        label: 'Viewer: Soft Shadows',
        category: 'Viewer',
        keywords: ['soft', 'shadows', 'pcss', 'viewer', 'toggle'],
        icon: Settings,
        getState: () => !!softShadowsEnabled,
        execute: () => onSoftShadowsToggle(!softShadowsEnabled),
      });
    }

    if (onFitToBounds) {
      commands.push({
        id: 'viewer.fit-to-bounds',
        label: 'Viewer: Fit to Bounds',
        category: 'Viewer',
        keywords: ['fit', 'zoom', 'bounds', 'center', 'viewer'],
        icon: Maximize,
        shortcut: getShortcut('viewer.fit-to-bounds'),
        execute: onFitToBounds,
      });
    }

    if (autoFit !== undefined && onAutoFitToggle) {
      commands.push({
        id: 'viewer.auto-fit',
        label: 'Viewer: Auto-Fit',
        category: 'Viewer',
        keywords: ['auto', 'fit', 'zoom', 'viewer', 'toggle'],
        icon: Compass,
        getState: () => !!autoFit,
        execute: () => onAutoFitToggle(!autoFit),
      });
    }

    // ─────────────────────────────────────────
    // PARAMETERS
    // ─────────────────────────────────────────

    if (onParametersChange && parameters.length > 0) {
      commands.push(
        {
          id: 'params.randomize',
          label: 'Randomize Parameters',
          category: 'Parameters',
          keywords: ['randomize', 'shuffle', 'parameters', 'random'],
          icon: Shuffle,
          shortcut: getShortcut('params.randomize'),
          execute: () => {
            const newParams = randomizeParameters(parameters);
            onParametersChange(newParams);
          },
          available: () => parameters.length > 0,
        },
        {
          id: 'params.reset',
          label: 'Reset Parameters to Default',
          category: 'Parameters',
          keywords: ['reset', 'default', 'parameters', 'restore'],
          icon: RefreshCcw,
          shortcut: getShortcut('params.reset'),
          execute: () => {
            const reset = parameters.map((p) => ({
              ...p,
              value: p.defaultValue,
            }));
            onParametersChange(reset);
          },
          available: () => parameters.length > 0,
        },
      );
    }

    if (onCreateSnapshot) {
      commands.push({
        id: 'params.create-snapshot',
        label: 'Create Parameter Snapshot',
        category: 'Parameters',
        keywords: ['snapshot', 'save', 'configuration', 'parameters'],
        icon: Camera,
        execute: onCreateSnapshot,
        available: () => parameters.length > 0,
      });
    }

    if (onImportParameters) {
      commands.push({
        id: 'params.import',
        label: 'Import Parameters from JSON',
        category: 'Parameters',
        keywords: ['import', 'json', 'parameters', 'load'],
        icon: Upload,
        execute: onImportParameters,
      });
    }

    // Download
    if (stlBlob) {
      commands.push({
        id: 'params.download-stl',
        label: 'Download STL',
        category: 'Parameters',
        keywords: ['download', 'stl', 'export', '3d print'],
        icon: Download,
        shortcut: getShortcut('params.download-stl'),
        execute: () => downloadSTLFile(stlBlob, undefined, [], parameters),
        available: () => !!stlBlob,
      });
    }

    if (scadCode) {
      commands.push({
        id: 'params.download-scad',
        label: 'Download SCAD',
        category: 'Parameters',
        keywords: ['download', 'scad', 'openscad', 'export', 'source'],
        icon: HardDriveDownload,
        execute: () => downloadOpenSCADFile(scadCode ?? '', undefined),
        available: () => !!scadCode,
      });
    }

    // ─────────────────────────────────────────
    // FILES
    // ─────────────────────────────────────────

    if (onUploadFile) {
      commands.push({
        id: 'files.upload',
        label: 'Upload File',
        category: 'Files',
        keywords: ['upload', 'file', 'import', 'add'],
        icon: Upload,
        execute: onUploadFile,
      });
    }

    if (onCaptureMesh) {
      commands.push({
        id: 'files.capture-mesh',
        label: 'Capture Mesh as STL',
        category: 'Files',
        keywords: ['capture', 'mesh', 'stl', 'file', 'save'],
        icon: HardDriveDownload,
        execute: onCaptureMesh,
      });
    }

    // ─────────────────────────────────────────
    // NAVIGATION
    // ─────────────────────────────────────────

    commands.push(
      {
        id: 'nav.new',
        label: 'New Model',
        category: 'Navigation',
        keywords: ['new', 'create', 'model', 'blank'],
        icon: FilePlus,
        execute: () => navigate('/new'),
      },
      {
        id: 'nav.explore',
        label: 'Explore Models',
        category: 'Navigation',
        keywords: ['explore', 'browse', 'gallery', 'public'],
        icon: Compass,
        execute: () => navigate('/explore'),
      },
      {
        id: 'nav.changelog',
        label: 'Changelog',
        category: 'Navigation',
        keywords: ['changelog', 'updates', 'release', 'news'],
        icon: BookOpen,
        execute: () => navigate('/changelog'),
      },
    );

    // Filter out unavailable commands
    return commands.filter((cmd) => cmd.available?.() !== false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigate,
    opts.onSave,
    opts.onSaveAs,
    opts.onFork,
    opts.onShare,
    opts.onScreenshot,
    opts.onShowHistory,
    opts.onAiAssist,
    opts.onToggleVisibility,
    opts.isPublic,
    opts.isOwner,
    opts.hasModel,
    opts.viewerState,
    opts.onViewerStateChange,
    opts.ssaoEnabled,
    opts.onSSAOToggle,
    opts.bloomEnabled,
    opts.onBloomToggle,
    opts.softShadowsEnabled,
    opts.onSoftShadowsToggle,
    opts.onFitToBounds,
    opts.autoFit,
    opts.onAutoFitToggle,
    opts.parameters,
    opts.onParametersChange,
    opts.onCreateSnapshot,
    opts.onImportParameters,
    opts.stlBlob,
    opts.scadCode,
    opts.onUploadFile,
    opts.onCaptureMesh,
    opts.onLayoutChange,
    opts.onProductMode,
    opts.onDevMode,
  ]);
}
